import React, { useEffect, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const PdfGenerator = ({ tableData, monthName, cusData }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfUrlData, setPdfUrlData] = useState(tableData[monthName?.month]);

  useEffect(() => {
    // Ensure that monthName and tableData are valid and that tableData[monthName?.month] is an object
    if (monthName?.month && tableData[monthName.month]) {
      setPdfUrlData(tableData[monthName.month]);
    } else {
      console.error("Invalid month or tableData for the given month");
    }
  }, [monthName, tableData]);

  const generatePDF = (action = "download") => {
    // Create a new PDF document
    const doc = new jsPDF();
    const totalPages = 1; // or dynamically determine total pages

    for (let i = 1; i <= totalPages; i++) {
      addHeader(doc);
      addWatermark(doc);
      addFooter(doc, i, totalPages);

      if (i < totalPages) doc.addPage();
    }
    // Add header - positioned at the very top
    //addHeader(doc);

    // Add tax return information - reduced spacing
    const infoEndY = addTaxReturnInfo(doc);

    // Add Part A - Tax Calculation - immediately after info with reduced spacing
    const partAEndY = addTaxCalculationImageStyle(doc, infoEndY);

    // Add Vendor's Compensation section - immediately after Part A
    const vendorEndY = addVendorCompensation(doc, partAEndY);

    // Add Bad Debt section - immediately after vendor comp
    const badDebtEndY = addBadDebt(doc, vendorEndY);

    // Add Part B - Sales Tax Distribution Table - immediately after bad debt
    addTaxDistribution(doc, badDebtEndY);

    if (action === "download") {
      // Save the PDF
      doc.save("sales_and_use_tax_return.pdf");
    } else if (action === "view") {
      // Create a data URL for viewing
      const pdfDataUri = doc.output("datauristring");
      setPdfUrl(pdfDataUri);
    }
  };

  const addHeader = (doc) => {
    // Top blue header bar - position at top with minimal margin
    doc.setFillColor(39, 119, 167);
    doc.rect(0, 5, 210, 12, "F");

    // Text color for header
    doc.setTextColor(255, 255, 255);

    // "Sales and Use Tax Return" positioned at flex-start (left side with margin)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("SALES AND USE TAX RETURN", 15, 13); // Left aligned at 15mm from left

    // Period text positioned at flex-end (right side)
    doc.setFontSize(9);
    const periodText = "Period: 01/01/2025 - 01/31/2025";
    const periodTextWidth =
      (doc.getStringUnitWidth(periodText) * 9) / doc.internal.scaleFactor;
    doc.text(periodText, 195 - periodTextWidth, 13); // Right aligned with 15mm margin from right

    // Reset text color
    doc.setTextColor(0, 0, 0);
  };
  const addFooter = (doc, pageNumber, totalPages) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const createdDate = new Date().toLocaleDateString("en-US");
    const fileCreatedDate = cusData?.file_created
      ? new Date(cusData.file_created).toLocaleDateString("en-US")
      : "N/A";

    doc.setFontSize(8);
    doc.setTextColor(100);

    // Page number - bottom-left
    doc.text(`Page ${pageNumber} of ${totalPages}`, 15, pageHeight - 10);

    // Created + File Created Date - bottom-right
    const rightText = `Created: ${createdDate} | File Created: ${fileCreatedDate}`;
    const textWidth = doc.getTextWidth(rightText);
    doc.text(rightText, pageWidth - 15 - textWidth, pageHeight - 10);
  };
  const addWatermark = (doc) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.saveGraphicsState();
    doc.setTextColor(200);
    doc.setFontSize(40);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(180, 180, 180);
    doc.text("CONFIDENTIAL", pageWidth / 2, pageHeight / 2, {
      angle: 45,
      align: "center",
    });
    doc.restoreGraphicsState();
  };
  const addTaxReturnInfo = (doc) => {
    // Reduced starting Y position
    const startY = 18;
    const infoY = startY + 5; // Reduced from 10 to 5

    // Left column labels
    doc.setFontSize(8);
    doc.text("S&U #:", 15, infoY);
    doc.text("Confirmation #:", 15, infoY + 5); // Reduced line spacing from 6 to 5
    doc.text("Filing Method:", 15, infoY + 10); // Reduced line spacing from 12 to 10

    // Right column labels
    doc.text("Business:", 105, infoY);
    doc.text("Address:", 105, infoY + 5); // Reduced line spacing from 6 to 5
    doc.text("Status:", 105, infoY + 10); // Reduced line spacing from 12 to 10

    // Left column values
    doc.setFont("helvetica", "normal");
    doc.text((monthName?.s_and_u_number ?? "N/A").toString(), 50, infoY);
    doc.text(monthName?.customer_file_no || "N/A", 50, infoY + 5); // Reduced line spacing
    doc.text("Online", 50, infoY + 10); // Reduced line spacing

    // Right column values
    doc.text(monthName?.company_name || "N/A", 145, infoY);
    doc.text(
      `${monthName?.city || ""}, ${monthName?.state_name || ""} ${
        monthName?.zipcode || ""
      }`.trim(),
      145,
      infoY + 5
    ); // Reduced line spacing
    doc.text(
      monthName?.file_status === "data_approved" ? "Completed" : "Pending",
      145,
      infoY + 10
    ); // Reduced line spacing

    return infoY + 12; // Reduced the bottom margin from 14 to 12
  };

  const addTaxCalculationImageStyle = (doc, startY) => {
    // Modern color palette based on the screenshot
    const colors = {
      header: { r: 31, g: 97, b: 141 }, // Header blue from screenshot
      text: { r: 40, g: 40, b: 40 }, // Near black for text
      highlight: { r: 70, g: 130, b: 180 }, // Steel blue for highlights
      line: { r: 200, g: 200, b: 200 }, // Light gray for lines
      finalAmount: { r: 0, g: 51, b: 102 }, // Darker blue for final amount
    };

    // Section header - PART A with styling matching screenshot
    doc.setFillColor(39, 119, 167);
    doc.rect(10, startY, 190, 10, "F"); // Rectangle matching screenshot
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("PART A - TAX CALCULATION", 15, startY + 5);

    // Reset text color to near black (not pure black for softer look)
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);

    // Get data for calculations
    const S_U_Return = pdfUrlData["S_&_U_Return"] || [];
    const totalSales =
      pdfUrlData?.Calculation_Total?.find((item) => item.Total_Sale)
        ?.Total_Sale || "0.00";
    const exemptSales =
      S_U_Return?.find((item) => item.Exampt_Sales)?.Exampt_Sales || "0.00";
    const taxableSales =
      S_U_Return?.find((item) => item.Taxabel_Sales)?.Taxabel_Sales || "0.00";
    const totalSalesTax =
      S_U_Return?.find((item) => item.Total_sales_tax)?.Total_sales_tax ||
      "0.00";
    const totalUseTax =
      S_U_Return?.find((item) => item.Total_S_U_Tax)?.Total_S_U_Tax || "0.00";
    const localSaleTax =
      pdfUrlData?.Calculation_Total?.find((item) => item.Local_Sale_Tax)
        ?.Local_Sale_Tax || "0.00";
    const totalTaxCollected =
      S_U_Return?.find((item) => item.Total_Tax_Collcted_4_5)
        ?.Total_Tax_Collcted_4_5 || "0.00";
    const vendorComp =
      pdfUrlData?.Vendor_Compensation?.find((item) => item.Total_Vendor_Comp)
        ?.Total_Vendor_Comp || "0.00";
    const totalAmountDue =
      pdfUrlData?.Vendor_Compensation?.find((item) => item.Total_Amount_Due)
        ?.Total_Amount_Due || "0.00";

    // Layout settings matching the screenshot
    const contentStartY = startY + 18;
    const lineHeight = 8;
    const labelX = 15;
    const centerX = 105; // Center point for horizontal lines
    const valueBoxX = 157;
    const valueBoxWidth = 38;
    const valueBoxHeight = 7;
    const lineWidth = 100; // Width of horizontal line, centered
    const midBoxX = 84; // Position for middle boxes (lines 7-8)
    const midBoxWidth = 38;

    // Function to draw a centered horizontal line with dots, matching screenshot
    const drawCenteredLine = (y) => {
      doc.setDrawColor(colors.line.r, colors.line.g, colors.line.b);
      doc.setLineWidth(0.3);

      // Draw dotted line
      const segmentLength = 1;
      const gapLength = 2;
      let currentX = centerX - lineWidth / 2;
      const endX = centerX + lineWidth / 2;

      while (currentX < endX) {
        doc.line(currentX, y, currentX + segmentLength, y);
        currentX += segmentLength + gapLength;
      }

      doc.setLineWidth(0.1);
      doc.setDrawColor(0, 0, 0); // Reset to default
    };

    // Draw form lines with modern styling
    doc.setFontSize(9);
    let currentY = contentStartY;

    // Line 1: Total Sales (State)
    doc.setFont("helvetica", "bold");
    doc.text("1 Total Sales (State)", labelX, currentY);
    doc.setFont("helvetica", "normal");

    // Draw dotted line to match screenshot
    drawCenteredLine(currentY - 1);

    // Draw box for all values
    doc.setDrawColor(150, 150, 150);
    doc.rect(valueBoxX, currentY - 6, valueBoxWidth, valueBoxHeight);
    doc.setDrawColor(0, 0, 0);

    // Display value right-aligned in box
    doc.text(
      formatNumber(totalSales),
      valueBoxX + valueBoxWidth - 2,
      currentY - 1,
      { align: "right" }
    );
    currentY += lineHeight;

    // Line 2: Exempt Sales
    doc.setFont("helvetica", "bold");
    doc.text("2 Exempt Sales", labelX, currentY);
    doc.setFont("helvetica", "normal");

    // Draw dotted line
    drawCenteredLine(currentY - 1);

    // Add minus indicator outside the box
    doc.text("--", valueBoxX - 3, currentY - 1);

    // Draw box
    doc.setDrawColor(150, 150, 150);
    doc.rect(valueBoxX, currentY - 6, valueBoxWidth, valueBoxHeight);
    doc.setDrawColor(0, 0, 0);

    // Display value
    doc.text(
      formatNumber(exemptSales),
      valueBoxX + valueBoxWidth - 2,
      currentY - 1,
      { align: "right" }
    );
    currentY += lineHeight;

    // Line 3: Taxable Sales
    doc.setFont("helvetica", "bold");
    doc.text("3 Taxable Sales", labelX, currentY);
    doc.setFont("helvetica", "normal");

    // Draw dotted line
    drawCenteredLine(currentY - 1);

    // Draw box with thin border matching screenshot
    doc.setDrawColor(150, 150, 150);
    doc.rect(valueBoxX, currentY - 6, valueBoxWidth, valueBoxHeight);
    doc.setDrawColor(0, 0, 0);

    // Display value
    doc.text(
      formatNumber(taxableSales),
      valueBoxX + valueBoxWidth - 2,
      currentY - 1,
      { align: "right" }
    );
    currentY += lineHeight;

    // Line 4: Total Sales Tax
    doc.setFont("helvetica", "bold");
    doc.text("4 Total Sales Tax", labelX, currentY);
    doc.setFont("helvetica", "normal");

    // Draw dotted line
    drawCenteredLine(currentY - 1);

    // Add plus indicator outside the box
    doc.text("+", valueBoxX - 3, currentY - 1);

    // Draw box
    doc.setDrawColor(150, 150, 150);
    doc.rect(valueBoxX, currentY - 6, valueBoxWidth, valueBoxHeight);
    doc.setDrawColor(0, 0, 0);

    // Display value
    doc.text(
      formatNumber(totalSalesTax),
      valueBoxX + valueBoxWidth - 2,
      currentY - 1,
      { align: "right" }
    );
    currentY += lineHeight;

    // Line 5: Total Use Tax
    doc.setFont("helvetica", "bold");
    doc.text("5 Total Use Tax", labelX, currentY);
    doc.setFont("helvetica", "normal");

    // Draw dotted line
    drawCenteredLine(currentY - 1);

    // Add plus indicator outside the box
    doc.text("+", valueBoxX - 3, currentY - 1);

    // Draw box with thin border matching screenshot
    doc.setDrawColor(150, 150, 150);
    doc.rect(valueBoxX, currentY - 6, valueBoxWidth, valueBoxHeight);
    doc.setDrawColor(0, 0, 0);

    // Display value
    doc.text(
      formatNumber(totalUseTax),
      valueBoxX + valueBoxWidth - 2,
      currentY - 1,
      { align: "right" }
    );
    currentY += lineHeight;

    // Line 6: Prepaid Local Sales/Use (MF)
    doc.setFont("helvetica", "bold");
    doc.text("6 Prepaid Local Sales/Use (MF)", labelX, currentY);
    doc.setFont("helvetica", "normal");

    // Draw dotted line
    drawCenteredLine(currentY - 1);

    // Add plus indicator - moved inside the box
    doc.text("+", valueBoxX - 3, currentY - 1);

    // Draw box with thin border matching screenshot
    doc.setDrawColor(150, 150, 150);
    doc.rect(valueBoxX, currentY - 6, valueBoxWidth, valueBoxHeight);
    doc.setDrawColor(0, 0, 0);

    // Display value
    doc.text(
      formatNumber(localSaleTax),
      valueBoxX + valueBoxWidth - 2,
      currentY - 1,
      { align: "right" }
    );
    currentY += lineHeight;

    // Line 7: Total Tax Collected - center-aligned value box
    doc.setFont("helvetica", "bold");
    doc.text("7 Total Tax Collected", labelX, currentY);
    doc.setFont("helvetica", "normal");

    // Draw box with thin border for center value (matching screenshot)
    doc.setDrawColor(150, 150, 150);
    doc.rect(midBoxX, currentY - 6, midBoxWidth, valueBoxHeight);
    doc.setDrawColor(0, 0, 0);

    // Display value
    doc.text(
      formatNumber(totalTaxCollected),
      midBoxX + midBoxWidth - 2,
      currentY - 1,
      { align: "right" }
    );
    currentY += lineHeight;

    // Line 8: Total Sales and Use Tax - center-aligned value box
    doc.setFont("helvetica", "bold");
    doc.text("8 Total Sales and Use Tax", labelX, currentY);
    doc.setFont("helvetica", "normal");

    // Draw box with thin border for center value (matching screenshot)
    doc.setDrawColor(150, 150, 150);
    doc.rect(midBoxX, currentY - 6, midBoxWidth, valueBoxHeight);
    doc.setDrawColor(0, 0, 0);

    // Display value
    doc.text(
      formatNumber(totalSalesTax),
      midBoxX + midBoxWidth - 2,
      currentY - 1,
      { align: "right" }
    );
    currentY += lineHeight;

    // Line 9: Excess Tax
    doc.setFont("helvetica", "bold");
    doc.text("9 Excess Tax", labelX, currentY);
    doc.setFont("helvetica", "normal");

    // Draw dotted line
    drawCenteredLine(currentY - 1);

    // Add plus indicator - moved inside the box
    doc.text("+", valueBoxX - 3, currentY - 1);

    // Draw box with thin border matching screenshot
    doc.setDrawColor(150, 150, 150);
    doc.rect(valueBoxX, currentY - 6, valueBoxWidth, valueBoxHeight);
    doc.setDrawColor(0, 0, 0);

    // Display value
    doc.text("0.00", valueBoxX + valueBoxWidth - 2, currentY - 1, {
      align: "right",
    });
    currentY += lineHeight;

    // Line 10: Vendor's Compensation
    doc.setFont("helvetica", "bold");
    doc.text("10 Vendor's Compensation", labelX, currentY);
    doc.setFont("helvetica", "normal");

    // Draw dotted line
    drawCenteredLine(currentY - 1);

    // Add minus indicator outside the box
    doc.text("--", valueBoxX - 3, currentY - 1);

    // Draw box with thin border matching screenshot
    doc.setDrawColor(150, 150, 150);
    doc.rect(valueBoxX, currentY - 6, valueBoxWidth, valueBoxHeight);
    doc.setDrawColor(0, 0, 0);

    // Display value
    doc.text(
      formatNumber(vendorComp),
      valueBoxX + valueBoxWidth - 2,
      currentY - 1,
      { align: "right" }
    );
    currentY += lineHeight;

    // Line 11: Previous Prepaid Amount
    doc.setFont("helvetica", "bold");
    doc.text("11 Previous Prepaid Amount", labelX, currentY);
    doc.setFont("helvetica", "normal");

    // Draw dotted line
    drawCenteredLine(currentY - 1);

    // Add minus indicator - moved inside the box
    doc.text("--", valueBoxX - 3, currentY - 1);

    // Draw box with thin border matching screenshot
    doc.setDrawColor(150, 150, 150);
    doc.rect(valueBoxX, currentY - 6, valueBoxWidth, valueBoxHeight);
    doc.setDrawColor(0, 0, 0);

    // Display value
    doc.text("0.00", valueBoxX + valueBoxWidth - 2, currentY - 1, {
      align: "right",
    });
    currentY += lineHeight;

    // Line 12: Current Prepaid Amount
    doc.setFont("helvetica", "bold");
    doc.text("12 Current Prepaid Amount", labelX, currentY);
    doc.setFont("helvetica", "normal");

    // Draw dotted line
    drawCenteredLine(currentY - 1);

    // Add plus indicator - moved inside the box
    doc.text("+", valueBoxX - 3, currentY - 1);

    // Draw box with thin border matching screenshot
    doc.setDrawColor(150, 150, 150);
    doc.rect(valueBoxX, currentY - 6, valueBoxWidth, valueBoxHeight);
    doc.setDrawColor(0, 0, 0);

    // Display value
    doc.text("0.00", valueBoxX + valueBoxWidth - 2, currentY - 1, {
      align: "right",
    });
    currentY += lineHeight;

    doc.setFont("helvetica", "bold");
    doc.text("13 Total Amount Due", labelX, currentY);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(
      colors.finalAmount.r,
      colors.finalAmount.g,
      colors.finalAmount.b
    );

    doc.setDrawColor(100, 100, 100); // Slightly bolder box for final value
    doc.rect(valueBoxX, currentY - 6, valueBoxWidth, valueBoxHeight);
    doc.setDrawColor(0, 0, 0);
    drawCenteredLine(currentY - 1);
    doc.text(
      formatNumber(totalAmountDue),
      valueBoxX + valueBoxWidth - 2,
      currentY - 1,
      { align: "right" }
    );

    // doc.line.r, colors.line.g, colors.line.b);
    doc.line(10, currentY + 2, 200, currentY + 2);
    doc.setLineWidth(0.1);
    doc.setDrawColor(0, 0, 0); // Reset to default

    return currentY + 5; // Return the final Y position with spacing
  };
  // Helper function to format numbers with commas and proper decimal places
  const formatNumber = (value) => {
    if (!value || isNaN(parseFloat(value))) return "0.00";
    return parseFloat(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const addVendorCompensation = (doc, startY) => {
    // Reduced header height
    doc.setFillColor(39, 119, 167);
    doc.rect(10, startY, 190, 7, "F"); // Reduced from 8 to 7
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("VENDOR'S COMPENSATION", 15, startY + 5);
    doc.setTextColor(0, 0, 0);

    const vendorCompHeaders = [
      "Description",
      "Tax Amount",
      "Tax Rate",
      "Vendor's Comp",
    ];

    const dynamicVendorCompData = pdfUrlData["Vendor_Compensation"] || [];
    const custData = cusData[monthName?.month];

    const vendorCompData = [
      [
        "Non Motor Fuel Sales/Use (First $3,000)",
        "3,000.00",
        custData?.Main?.vendor_comp_above,
        (
          dynamicVendorCompData.find((d) => d.Non_MF_Sales_1st !== undefined)
            ?.Non_MF_Sales_1st || 0
        ).toFixed(2),
      ],
      [
        "Non Motor Fuel Sales/Use (Over $3,000)",
        "0.00",
        custData?.Main?.vendor_comp_below,
        (
          dynamicVendorCompData.find((d) => d.Non_MF_Sales_Aft !== undefined)
            ?.Non_MF_Sales_Aft || 0
        ).toFixed(2),
      ],
      ["On Road Motor Fuel", "0.00", custData?.Main?.vendor_comp_above, "0.00"],
      [
        "Off Road Motor Fuel",
        "0.00",
        custData?.Main?.vendor_comp_above,
        "0.00",
      ],
      [
        "Total Vendor's Compensation",
        "",
        "",
        (
          dynamicVendorCompData.find((d) => d.Total_Vendor_Comp !== undefined)
            ?.Total_Vendor_Comp || 0
        ).toFixed(2),
      ],
    ];

    try {
      autoTable(doc, {
        startY: startY + 8, // Reduced from 9 to 8
        head: [vendorCompHeaders], // wrap headers inside an array
        body: vendorCompData,
        theme: "grid",
        headStyles: {
          fillColor: [70, 107, 158], // Header background
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "center",
          cellPadding: 1,
          fontSize: 8,
        },
        styles: {
          cellPadding: 1,
          fontSize: 8,
          lineWidth: 0.1,
          valign: "middle", // vertically center
        },
        columnStyles: {
          0: { cellWidth: 80, halign: "left" }, // Description: left aligned
          1: { cellWidth: 40, halign: "right" }, // Tax Amount: right aligned
          2: { cellWidth: 30, halign: "center" }, // Tax Rate: center aligned
          3: { cellWidth: 40, halign: "right" }, // Vendor's Comp: right aligned
        },
        margin: { left: 10, right: 10 },
        rowPageBreak: "avoid",
      });

      return doc.lastAutoTable.finalY + 1; // Reduced spacing from 2 to 1
    } catch (error) {
      console.error("Error in vendor compensation table:", error);
      return startY + 35; // Reduced from 40 to 35
    }
  };

  const addBadDebt = (doc, startY) => {
    // Section header - compressed
    doc.setFillColor(39, 119, 167);
    doc.rect(10, startY, 190, 7, "F"); // Reduced from 8 to 7
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("BAD DEBT", 15, startY + 5);

    // Reset text color
    doc.setTextColor(0, 0, 0);

    // Table data
    const badDebtData = [
      ["Bad Debt Credit", "", "0.00"],
      ["Bad Debt Collected", "", "0.00"],
    ];

    try {
      autoTable(doc, {
        startY: startY + 8, // Reduced from 9 to 8
        head: [], // No header
        body: badDebtData,
        theme: "plain",
        styles: {
          cellPadding: 1, // Reduced padding
          fontSize: 8, // Smaller font
          lineWidth: 0.1, // Thinner lines
        },
        columnStyles: {
          0: { cellWidth: 100 },
          1: { cellWidth: 50 },
          2: { cellWidth: 40, halign: "right" },
        },
        margin: { left: 10, right: 10 },
        rowPageBreak: "avoid",
      });

      return doc.lastAutoTable.finalY + 1; // Reduced from 2 to 1
    } catch (error) {
      console.error("Error in bad debt table:", error);
      return startY + 18; // Reduced from 20 to 18
    }
  };
  const addTaxDistribution = (doc, startY) => {
    // Reduced header height
    doc.setFillColor(39, 119, 167);
    doc.rect(10, startY, 190, 7, "F"); // Reduced from 8 to 7
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("PART B - SALES TAX DISTRIBUTION TABLE", 15, startY + 5);
    doc.setTextColor(0, 0, 0);

    const distributionHeaders = [
      "Jurisdiction",
      "Taxable Sales",
      "Tax Rate",
      "Tax Due",
    ];

    const S_U_Return = pdfUrlData["Par_B_S_U_Distribution"] || [];
    const custData = cusData[monthName?.month];

    const distributionData = [
      [
        "State",
        Number(
          S_U_Return?.find((item) => item.State)?.State || 0
        ).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        Number(cusData.state_rate / 100).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        Number(
          S_U_Return?.find((item) => item.State_Tax)?.State_Tax || 0
        ).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      ],
      [
        cusData.county_name || " ",
        Number(
          S_U_Return?.find((item) => item.County)?.County || 0
        ).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        Number(cusData.county_rate / 100).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        Number(
          (S_U_Return?.find((item) => item.County)?.County || 0) *
            (cusData.county_rate / 100)
        ).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      ],
      [
        "Total Tax",
        "",
        "",
        Number(
          S_U_Return?.find((item) => item.Total_Tax)?.Total_Tax || 0
        ).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      ],
    ];

    try {
      autoTable(doc, {
        startY: startY + 8, // Reduced from 9 to 8
        head: [distributionHeaders], // wrap headers inside an array
        body: distributionData,
        theme: "grid",
        headStyles: {
          fillColor: [70, 107, 158], // Header background
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "center",
          cellPadding: 1,
          fontSize: 8,
        },
        styles: {
          cellPadding: 1,
          fontSize: 8,
          lineWidth: 0.1,
          valign: "middle", // vertically center
        },
        columnStyles: {
          0: { cellWidth: 80, halign: "left" }, // Description: left aligned
          1: { cellWidth: 40, halign: "right" }, // Tax Amount: right aligned
          2: { cellWidth: 30, halign: "center" }, // Tax Rate: center aligned
          3: { cellWidth: 40, halign: "right" }, // Vendor's Comp: right aligned
        },
        margin: { left: 10, right: 10 },
        rowPageBreak: "avoid",
      });

      return doc.lastAutoTable.finalY + 1; // Reduced spacing from 2 to 1
    } catch (error) {
      console.error("Error in vendor compensation table:", error);
      return startY + 35; // Reduced from 40 to 35
    }
  };

  // Close the PDF viewer
  const closePdfViewer = () => {
    setPdfUrl(null);
  };

  return (
    <>
      <div className="pdf-buttons">
        <button
          type="button"
          className="usa-button usa-button--outline margin-right-2"
          onClick={() => generatePDF("view")}
        >
          View PDF
        </button>

        <button
          type="button"
          className="usa-button"
          onClick={() => generatePDF("download")}
        >
          Download PDF
        </button>
      </div>

      {/* PDF Viewer Modal */}
      {pdfUrl && (
        <div
          className="pdf-modal"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.7)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            className="pdf-container"
            style={{
              width: "90%",
              height: "90%",
              backgroundColor: "white",
              position: "relative",
            }}
          >
            <button
              onClick={closePdfViewer}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                zIndex: 1001,
                background: "white",
                border: "1px solid #ccc",
                borderRadius: "50%",
                width: "30px",
                height: "30px",
                fontSize: "16px",
                cursor: "pointer",
              }}
            >
              ×
            </button>
            <iframe
              src={pdfUrl}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
              title="PDF Viewer"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default PdfGenerator;
